import { Seo } from "@/components/Seo";
import { Reveal, PillButton, Hairline, GlowFrame } from "@/components/primitives";
import { StewardStoneNav } from "@/components/StewardStoneNav";
import { InquiryForm } from "@/components/InquiryForm";

const CAPS = [
  { to: "/steward-and-stone/healthcare-advisory", label: "Healthcare Advisory", testid: "ss-cap-healthcare", d: "Our founding specialization: examining hospital and healthcare operations to identify the structural, strategic, and workflow conditions underlying persistent difficulties." },
  { to: "/steward-and-stone/operational-strategy", label: "Operational Strategy", testid: "ss-cap-operational", d: "Organizational and operational assessment, workflow and gap analysis, systems redesign, and practical implementation planning across complex environments." },
  { to: "/steward-and-stone/assessment", label: "Organizational Assessment", testid: "ss-cap-assessment", d: "A structured examination of organizational practice, identifying where value erodes, risk accumulates, and substantive improvement is feasible." },
];

export default function StewardStone() {
  return (
    <div className="glow-navy jmh-page jmh-division-page">
      <Seo title="Steward & Stone — Strategic & Operational Advisory" description="Steward & Stone — the strategic and operational advisory company of Jennae Michelle Holdings. Healthcare operations advisory, operational strategy, and organizational assessment for complex problems." />
      <StewardStoneNav />

      <section className="text-center jmh-section jmh-page-opening">
        <Reveal>
          <h1 className="sr-only">Steward &amp; Stone</h1>
          <img src="/brand/steward-stone-web.png" alt="Steward & Stone — Executive Advisory" className="mx-auto w-full jmh-page-brand" />
        </Reveal>
        <Reveal delay={0.12}>
          <div className="mt-8 mx-auto space-y-5 text-lg md:text-xl text-[var(--bone)]/85 leading-relaxed text-left jmh-content-shell">
            <p>An organization’s reported performance can conceal the strain required to sustain it. Employees may be compensating for structural weaknesses through exhaustion, informal workarounds, and a sense of responsibility that the operation has come to depend on.</p>
            <p>Over time, the distance between an organization’s intended processes and its actual working conditions becomes consequential. Employees absorb the demands, customers and patients experience interruptions, and leadership sees outcomes whose underlying causes may remain obscured.</p>
            <p className="text-center font-serif2 italic text-[var(--bone)]">Steward &amp; Stone examines that distance.</p>
            <p>We examine the decisions, workflows, expectations, resources, and relationships that shape daily operations. An effective recommendation must account for the conditions in which it will be implemented. Our purpose is to identify why difficulties persist and develop changes that leadership and employees can carry into practice together.</p>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-5 mx-auto text-sm md:text-base text-[var(--bone)]/70 leading-relaxed">
            Healthcare and hospital operations are our founding specialization — the capability itself extends to any organization whose systems, workflows, and performance can be assessed and improved.
          </p>
        </Reveal>
      </section>

      <section className="jmh-section">
        <div className="mx-auto jmh-content-shell">
          <Reveal><Hairline className="opacity-70 mb-12" /></Reveal>
          <div className="jmh-content-grid">
            {CAPS.map((c, i) => (
              <Reveal key={c.to} delay={i * 0.06} className="h-full">
                <GlowFrame to={c.to} testid={c.testid} innerClassName="corner-frame px-7 py-8 h-full flex flex-col transition-transform duration-500 group-hover:-translate-y-[2px]">
                  <h3 className="font-serif2 text-xl md:text-2xl text-[var(--bone)] leading-tight">{c.label}</h3>
                  <p className="mt-4 text-sm md:text-base text-[var(--bone)]/75 leading-relaxed">{c.d}</p>
                  <div className="mt-5 font-display uppercase tracking-wide2 text-[10px] text-[var(--gold-lite)] opacity-0 group-hover:opacity-100 transition-opacity">Enter →</div>
                </GlowFrame>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="text-center jmh-section">
        <Reveal>
          <h2 className="font-display gold-text uppercase tracking-wide2 text-2xl md:text-4xl leading-snug">When Only One Succeeds,<br />None Succeed</h2>
        </Reveal>
        <Reveal delay={0.1} className="mt-8 mx-auto text-left jmh-content-shell">
          <p className="font-display uppercase tracking-luxe text-[10px] text-[var(--gold)]/80 text-center mb-6">Success Has More Than One Participant</p>
          <div className="space-y-5 text-lg md:text-xl text-[var(--bone)]/85 leading-relaxed">
            <p>Organizational success carries several responsibilities: financial viability, the well-being and capability of the team, the needs of those relying on its services, and the consequences its decisions create for the surrounding community.</p>
            <p>These responsibilities can create competing demands. Considering them together makes those tensions visible and allows leadership to assess the consequences of a decision before others are left to absorb them.</p>
            <p>Profit alone cannot demonstrate that an organization is operating responsibly or sustainably. Lasting value requires attention to how that value is produced, who benefits from it, and who bears its costs.</p>
          </div>
        </Reveal>
        <Reveal delay={0.16} className="mt-10"><PillButton to="/steward-and-stone/assessment" testid="ss-request-review">Request a Review</PillButton></Reveal>
      </section>

      <section id="request" className="jmh-section">
        <div className="mx-auto jmh-content-shell">
          <Reveal className="text-center mb-10"><p className="font-display uppercase tracking-luxe text-xs text-[var(--muted)]">Confidential Inquiry</p></Reveal>
          <Reveal delay={0.05}>
            <InquiryForm type="review" division="Steward & Stone" cta="Request a Review" testid="ss-review-form" />
          </Reveal>
        </div>
      </section>

      <div className="pb-16 text-center px-6">
        <p className="font-display uppercase tracking-wide2 text-[10px] md:text-xs text-[var(--muted)] leading-relaxed">
          Steward &amp; Stone provides strategic and operational advisory only.<br />All inquiries are reviewed confidentially and a response is provided at our discretion.
        </p>
      </div>
    </div>
  );
}
